/**
 * Copyright (c) 2019 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __extends } from "tslib";
import { PluginBehavior } from '../../../behavior';
import { ParamDefinition as PD } from '../../../../../mol-util/param-definition';
import { ValenceModelProvider } from '../../../../../mol-model-props/computed/valence-model';
import { PluginStateObject } from '../../../../../mol-plugin-state/objects';
import { StateSelection } from '../../../../../mol-state';
import { StructureElement } from '../../../../../mol-model/structure';
import { OrderedSet } from '../../../../../mol-data/int';
import { geometryLabel } from '../../../../../mol-model-props/computed/chemistry/geometry';
import { arraySetAdd } from '../../../../../mol-util/array';
export var ValenceModel = PluginBehavior.create({
  name: 'computed-valence-model-prop',
  category: 'custom-props',
  display: {
    name: 'Valence Model'
  },
  ctor:
  /** @class */
  function (_super) {
    __extends(class_1, _super);

    function class_1() {
      var _this = _super !== null && _super.apply(this, arguments) || this;

      _this.provider = ValenceModelProvider;
      _this.labelProvider = {
        label: function (loci) {
          if (!_this.params.showTooltip) return void 0;

          switch (loci.kind) {
            case 'element-loci':
              if (loci.elements.length === 0) return void 0;
              var labels = [];

              var structures = _this.getStructures(loci.structure);

              for (var _i = 0, structures_1 = structures; _i < structures_1.length; _i++) {
                var s = structures_1[_i];

                var valenceModel = _this.provider.get(s).value;

                if (!valenceModel) continue;
                var l = StructureElement.Loci.remap(loci, s);
                if (l.elements.length !== 1) continue;
                var e = l.elements[0];
                if (OrderedSet.size(e.indices) !== 1) continue;
                var vm = valenceModel.get(e.unit.id);
                if (!vm) continue;
                var idx = OrderedSet.start(e.indices);
                var charge = vm.charge[idx];
                var idealGeometry = vm.idealGeometry[idx];
                var implicitH = vm.implicitH[idx];
                var totalH = vm.totalH[idx];
                labels.push("Valence Model: <small>Charge</small> " + charge + " | <small>Ideal Geometry</small> " + geometryLabel(idealGeometry) + " | <small>Implicit H</small> " + implicitH + " | <small>Total H</small> " + totalH);
              }

              return labels.length ? labels.join('<br/>') : undefined;

            default:
              return void 0;
          }
        }
      };
      return _this;
    }

    class_1.prototype.getStructures = function (structure) {
      var structures = [];
      var root = this.ctx.helpers.substructureParent.get(structure);

      if (root) {
        var state = this.ctx.state.data;
        var selections = state.select(StateSelection.Generators.ofType(PluginStateObject.Molecule.Structure, root.transform.ref));

        for (var _i = 0, selections_1 = selections; _i < selections_1.length; _i++) {
          var s = selections_1[_i];
          if (s.obj) arraySetAdd(structures, s.obj.data);
        }
      }

      return structures;
    };

    class_1.prototype.update = function (p) {
      var updated = this.params.autoAttach !== p.autoAttach || this.params.showTooltip !== p.showTooltip;
      this.params.autoAttach = p.autoAttach;
      this.params.showTooltip = p.showTooltip;
      this.ctx.customStructureProperties.setDefaultAutoAttach(this.provider.descriptor.name, this.params.autoAttach);
      return updated;
    };

    class_1.prototype.register = function () {
      this.ctx.customStructureProperties.register(this.provider, this.params.autoAttach);
      this.ctx.managers.lociLabels.addProvider(this.labelProvider);
    };

    class_1.prototype.unregister = function () {
      this.ctx.customStructureProperties.unregister(this.provider.descriptor.name);
      this.ctx.managers.lociLabels.removeProvider(this.labelProvider);
    };

    return class_1;
  }(PluginBehavior.Handler),
  params: function () {
    return {
      autoAttach: PD.Boolean(false),
      showTooltip: PD.Boolean(true)
    };
  }
});